import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import StructuredData from '@/components/base/StructuredData';

const steps = [
  {
    number: '01',
    icon: 'ri-calendar-check-line',
    title: 'Subscribe & Start Your 7-Day Window',
    desc: 'Your 7-day satisfaction guarantee begins the moment your subscription is activated. You have a full 7 calendar days from the activation date to evaluate the service.',
    note: 'The 7-day window starts on your activation date, not your payment date.',
  },
  {
    number: '02',
    icon: 'ri-customer-service-2-line',
    title: 'Contact Our Support Team',
    desc: 'If you are not satisfied for any reason, reach out to our support team via WhatsApp or email before the 7-day window expires. Simply let us know you would like a refund.',
    note: 'Contact us at support@4k-dream.com or WhatsApp +1 (205) 709 2376.',
  },
  {
    number: '03',
    icon: 'ri-file-list-3-line',
    title: 'Provide Your Order Details',
    desc: 'Share your order confirmation number, the email address used at purchase, and a brief reason for your refund request. No lengthy justification is required — we just ask for basic details.',
    note: 'Your order number can be found in your payment confirmation email.',
  },
  {
    number: '04',
    icon: 'ri-shield-check-line',
    title: 'Refund Reviewed & Approved',
    desc: 'Our team will review your request within 24 hours. Eligible refund requests are approved without dispute. We honor our guarantee — no questions asked.',
    note: 'You will receive a confirmation email once your refund is approved.',
  },
  {
    number: '05',
    icon: 'ri-bank-card-line',
    title: 'Refund Processed to Your Original Payment Method',
    desc: 'Once approved, your full refund is processed back to the original payment method used at checkout. Processing times vary by payment provider.',
    note: 'PayPal: 1–3 business days. Credit/debit card: 3–7 business days. Bank transfer: 5–10 business days.',
  },
];

const eligibility = [
  { icon: 'ri-checkbox-circle-line', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', title: 'Eligible for Refund', items: [
    'Request submitted within 7 days of activation',
    'First-time subscription purchase',
    'Service not meeting advertised quality standards',
    'Technical issues unresolved by our support team',
    'Accidental duplicate purchase',
  ]},
  { icon: 'ri-close-circle-line', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20', title: 'Not Eligible for Refund', items: [
    'Request submitted after the 7-day window has expired',
    'Account suspended due to Terms of Service violations',
    'Renewal subscriptions (applies to first purchase only)',
    'Free trial accounts (no charge was made)',
    'Refund already issued on a previous subscription',
  ]},
];

const faqs = [
  { q: 'Do I need to give a reason for my refund?', a: 'No. We honor our 7-day guarantee without requiring a detailed explanation. A simple request is sufficient.' },
  { q: 'Can I get a partial refund?', a: 'Our policy covers a full refund of the amount paid. We do not offer partial refunds for unused days beyond the 7-day window.' },
  { q: 'What if I subscribed through a third party?', a: 'If you purchased through a third-party reseller, please contact them directly. Our refund policy applies only to purchases made directly through 4K Dream.' },
  { q: 'How will I know my refund was processed?', a: 'You will receive a confirmation email from us once the refund is approved and initiated. Your bank or payment provider will then process it within their standard timeframe.' },
  { q: 'Can I re-subscribe after a refund?', a: 'Yes, you are welcome to re-subscribe at any time. However, the 7-day guarantee applies only to your first purchase and will not apply to subsequent subscriptions.' },
];

const refundSchemas = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Refund Policy – 4K Dream',
    url: 'https://www.4k-dream.com/refund',
    description: '7-day money-back guarantee. Full refund policy for 4K Dream streaming service — no questions asked.',
    dateModified: '2026-04-03',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.4k-dream.com/' },
        { '@type': 'ListItem', position: 2, name: 'Refund Policy', item: 'https://www.4k-dream.com/refund' },
      ],
    },
    publisher: {
      '@type': 'Organization',
      name: '4K Dream',
      url: 'https://www.4k-dream.com',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Do I need to give a reason for my refund?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'No. We honor our 7-day guarantee without requiring a detailed explanation. A simple request is sufficient.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I get a partial refund?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our policy covers a full refund of the amount paid. We do not offer partial refunds for unused days beyond the 7-day window.',
        },
      },
      {
        '@type': 'Question',
        name: 'How long does a refund take?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'PayPal: 1–3 business days. Credit/debit card: 3–7 business days. Bank transfer: 5–10 business days.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I re-subscribe after a refund?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, you are welcome to re-subscribe at any time. However, the 7-day guarantee applies only to your first purchase.',
        },
      },
    ],
  },
];

const RefundPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <StructuredData schemas={refundSchemas} id="refund" />
      {/* Header */}
      <header className="bg-black/80 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              alt="4K Dream Logo"
              className="h-9 w-auto object-contain"
              src="https://static.readdy.ai/image/5c7545dbb6198b28906c423b0244c4c9/0789dc6d730dd15b6f1e8e24be7c3580.webp"
            />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0a1a0a] via-[#0d0d1a] to-[#0a0a0f] py-16 sm:py-20 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full px-4 py-2 mb-6">
            <i className="ri-shield-check-line text-emerald-400 text-sm"></i>
            <span className="text-emerald-400 text-sm font-medium">Satisfaction Guaranteed</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-white">Refund Policy</h1>
          <p className="text-white/60 text-lg mb-6">Last updated: April 3, 2026</p>
          <p className="text-white/80 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            We stand behind the quality of our service. If you are not completely satisfied within the first <strong className="text-emerald-400">7 days</strong> of your subscription, we will issue a full refund — no questions asked.
          </p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20 space-y-16">

        {/* Guarantee Banner */}
        <div className="bg-gradient-to-r from-emerald-500/15 to-teal-500/10 border border-emerald-500/30 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0">
            <i className="ri-shield-check-line text-white text-4xl"></i>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">7-Day Money-Back Guarantee</h2>
            <p className="text-white/70 leading-relaxed">
              Our 7-day money-back guarantee is unconditional. If for any reason you are not satisfied with our streaming service during the first 7 days, simply contact us and we will process your full refund promptly. No hidden conditions, no disputes.
            </p>
          </div>
        </div>

        {/* Step-by-step process */}
        <section>
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">How to Request a Refund</h2>
            <p className="text-white/60 text-base max-w-xl mx-auto">Follow these simple steps to receive your full refund within the 7-day window.</p>
          </div>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-emerald-500/50 via-emerald-500/20 to-transparent hidden sm:block"></div>

            <div className="space-y-6">
              {steps.map((step, i) => (
                <div key={step.number} className="relative flex gap-6 sm:gap-8">
                  {/* Step circle */}
                  <div className="flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center z-10 shadow-lg shadow-emerald-500/20">
                    <i className={`${step.icon} text-white text-xl`}></i>
                  </div>

                  {/* Content */}
                  <div className={`flex-1 bg-white/5 border border-white/10 rounded-2xl p-5 sm:p-6 hover:border-emerald-500/30 transition-all duration-300 ${i === steps.length - 1 ? '' : 'mb-2'}`}>
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <h3 className="text-lg sm:text-xl font-bold text-white leading-tight">{step.title}</h3>
                      <span className="text-3xl font-black text-emerald-500/30 flex-shrink-0 leading-none">{step.number}</span>
                    </div>
                    <p className="text-white/70 leading-relaxed mb-3 text-sm sm:text-base">{step.desc}</p>
                    <div className="flex items-start gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-3">
                      <i className="ri-information-line text-emerald-400 flex-shrink-0 mt-0.5 text-sm"></i>
                      <p className="text-emerald-300/80 text-xs sm:text-sm leading-relaxed">{step.note}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Eligibility */}
        <section>
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Refund Eligibility</h2>
            <p className="text-white/60 text-base max-w-xl mx-auto">Please review the conditions below to confirm your refund eligibility.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {eligibility.map((col) => (
              <div key={col.title} className={`border rounded-2xl p-6 ${col.bg}`}>
                <div className="flex items-center gap-3 mb-5">
                  <i className={`${col.icon} ${col.color} text-2xl`}></i>
                  <h3 className={`text-lg font-bold ${col.color}`}>{col.title}</h3>
                </div>
                <ul className="space-y-3">
                  {col.items.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-white/70 text-sm leading-relaxed">
                      <i className={`${col.icon} ${col.color} flex-shrink-0 mt-0.5 text-sm`}></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Timelines */}
        <section>
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Refund Processing Times</h2>
            <p className="text-white/60 text-base max-w-xl mx-auto">Once approved, refunds are processed according to your payment method.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: 'ri-paypal-line', method: 'PayPal', time: '1–3 business days', color: 'from-[#003087]/30 to-[#009cde]/20', border: 'border-[#009cde]/20' },
              { icon: 'ri-bank-card-line', method: 'Credit / Debit Card', time: '3–7 business days', color: 'from-[#862ce0]/20 to-[#c026d3]/10', border: 'border-[#862ce0]/20' },
              { icon: 'ri-building-line', method: 'Bank Transfer', time: '5–10 business days', color: 'from-amber-500/15 to-orange-500/10', border: 'border-amber-500/20' },
            ].map((p) => (
              <div key={p.method} className={`bg-gradient-to-br ${p.color} border ${p.border} rounded-2xl p-6 text-center`}>
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-xl bg-white/10">
                  <i className={`${p.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="font-bold text-white mb-2">{p.method}</h3>
                <p className="text-white/60 text-sm">{p.time}</p>
              </div>
            ))}
          </div>
          <p className="text-white/40 text-xs text-center mt-4">Processing times are estimates and may vary depending on your financial institution.</p>
        </section>

        {/* FAQ */}
        <section>
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.q} className="bg-white/5 border border-white/10 rounded-xl p-5 sm:p-6 hover:border-emerald-500/30 transition-all">
                <h3 className="font-semibold text-white mb-2 flex items-start gap-3">
                  <i className="ri-question-line text-emerald-400 flex-shrink-0 mt-0.5"></i>
                  {faq.q}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed pl-6">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Contact CTA */}
        <section>
          <div className="bg-gradient-to-br from-[#862ce0]/20 to-[#c026d3]/10 border border-[#862ce0]/30 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-3">Ready to Request a Refund?</h2>
            <p className="text-white/70 mb-8 max-w-lg mx-auto">Contact our support team through any of the channels below. We aim to respond within 24 hours and process all eligible refunds promptly.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a
                href="https://wa.me/12057092376"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold px-8 py-4 rounded-xl transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                WhatsApp Support
              </a>
              <a
                href="mailto:support@4k-dream.com"
                className="inline-flex items-center justify-center gap-2 border border-[#862ce0]/50 text-[#b47ef3] hover:bg-[#862ce0]/20 font-semibold px-8 py-4 rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-line text-xl"></i>
                support@4k-dream.com
              </a>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-white/50 text-sm">
              <span className="flex items-center gap-2"><i className="ri-time-line text-emerald-400"></i> Response within 24 hours</span>
              <span className="flex items-center gap-2"><i className="ri-shield-check-line text-emerald-400"></i> No dispute, no hassle</span>
              <span className="flex items-center gap-2"><i className="ri-money-dollar-circle-line text-emerald-400"></i> Full refund guaranteed</span>
            </div>
          </div>
        </section>

        {/* Bottom nav */}
        <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors cursor-pointer text-sm">
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-white/50 hover:text-[#b47ef3] transition-colors text-sm cursor-pointer">Privacy Policy</Link>
            <Link to="/terms" className="text-white/50 hover:text-[#b47ef3] transition-colors text-sm cursor-pointer">Terms &amp; Conditions</Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black/50 border-t border-white/10 py-6 px-4 text-center text-white/40 text-sm">
        <p>4K Dream 2019-2026 © All Rights Reserved &nbsp;·&nbsp;
          <Link to="/privacy" className="hover:text-[#b47ef3] transition-colors">Privacy Policy</Link>
          &nbsp;·&nbsp;
          <Link to="/terms" className="hover:text-[#b47ef3] transition-colors">Terms &amp; Conditions</Link>
          &nbsp;·&nbsp;
          <Link to="/refund" className="hover:text-emerald-400 transition-colors">Refund Policy</Link>
        </p>
      </footer>
    </div>
  );
};

export default RefundPolicy;
